'use client';
import React, { useState, useContext } from 'react';
import styles from './RequestTimeModal.module.css';
import { UserContext } from '../context/UserContext';

const TIMEZONE_OPTIONS = [
  { value: '', label: 'Select your time zone...' },
  { value: 'Eastern Time (ET)', label: 'Eastern Time (ET)' },
  { value: 'Central Time (CT)', label: 'Central Time (CT)' },
  { value: 'Mountain Time (MT)', label: 'Mountain Time (MT)' },
  { value: 'Pacific Time (PT)', label: 'Pacific Time (PT)' },
  { value: 'Alaska Time (AKT)', label: 'Alaska Time (AKT)' },
  { value: 'Hawaii Time (HT)', label: 'Hawaii Time (HT)' },
  { value: 'Other', label: 'Other' },
];

export default function RequestTimeModal({
  isOpen,
  onClose,
  courseName,
  courseId,
  teacherName,
  teacherEmail,
}) {
  const user = useContext(UserContext);

  const [message, setMessage] = useState('');
  const [timezone, setTimezone] = useState('');
  const [otherTimezone, setOtherTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setMessage('');
    setTimezone('');
    setOtherTimezone('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const effectiveTimezone = timezone === 'Other' ? otherTimezone.trim() : timezone;

    if (!effectiveTimezone) {
      setError('Please select a time zone.');
      return;
    }

    setLoading(true);

    try {
      const body = {
        courseId,
        courseName,
        teacherName,
        teacherEmail,
        message: message.trim(),
        timezone: effectiveTimezone,
      };

      const response = await fetch('/api/request-time', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit request.');
      }

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalDialog} onClick={(e) => e.stopPropagation()}>
          <div className={styles.successMessage}>
            Your request has been sent! The teacher will be notified.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalDialog} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={handleClose} aria-label="Close">
          &times;
        </button>

        <div className={styles.modalHeader}>
          <h3>Request a Different Time</h3>
          <p>Let the teacher know what times work better for you.</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.formGroup}>
            <label htmlFor="timezone">
              Time Zone<span className={styles.required}>*</span>
            </label>
            <select
              id="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {timezone === 'Other' && (
              <input
                className={styles.otherTimezoneInput}
                type="text"
                value={otherTimezone}
                onChange={(e) => setOtherTimezone(e.target.value)}
                placeholder="Enter your time zone"
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="message">Message (optional)</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Weekday evenings after 5 PM work best for us..."
            />
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.btnSubmit}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
